import {Suspense, type PropsWithChildren} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ForYouItemError, ForYouItemSkeleton} from './ForYouItemFallbacks'
import {useLayoutAuthorLogin} from '../../../../stores/layout-selectors'

export type ForYouAudience = 'author' | 'reviewer'

/** Whether the current viewer is the pull request author. */
export function useViewerIsPullRequestAuthor(): boolean {
  const authorLogin = useLayoutAuthorLogin()
  const viewerLogin = getCurrentUserLogin()
  return !!viewerLogin && authorLogin === viewerLogin
}

/**
 * Error boundary + suspense wrapper for a single ForYou item. When `audience` is
 * provided, the item only renders for the matching viewer (author vs. everyone
 * else); otherwise it always renders.
 */
export function ForYouItemBoundary({
  children,
  name,
  audience,
}: PropsWithChildren<{name: string; audience?: ForYouAudience}>) {
  const boundary = (
    <ErrorBoundary boundaryName={`ForYouItem.${name}`} fallback={<ForYouItemError />}>
      <Suspense fallback={<ForYouItemSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  )

  return audience ? <AudienceGate audience={audience}>{boundary}</AudienceGate> : boundary
}

/** Renders its children only when the current viewer matches `audience`. */
function AudienceGate({audience, children}: PropsWithChildren<{audience: ForYouAudience}>) {
  const viewerIsAuthor = useViewerIsPullRequestAuthor()
  const matchesAudience = audience === 'author' ? viewerIsAuthor : !viewerIsAuthor

  return matchesAudience ? <>{children}</> : null
}
